import React from 'react';
import SEOHelmet from '../SEOHelmet';

const BulandshahrPage: React.FC = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
    //eslint-disable-next-line
  }, []);
  
  return (
    <div className='mb-20 mt-28 max-w-4xl mx-auto px-4'>
      <SEOHelmet />
      <h1 className='text-3xl md:text-5xl font-bold text-center mb-8'>Neurosurgical Consultations for Bulandshahr | Dr. Gangesh Gunjan</h1>
      
      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Specialized Neurosurgical Care for Bulandshahr Patients</h2>
        <p className='text-lg text-gray-700 leading-relaxed'>Dr. Gangesh Gunjan provides comprehensive neurosurgical consultations and surgical evaluations for patients travelling from Bulandshahr and surrounding areas. Located at Sarvodaya Hospital, Greater Noida West, Dr. Gunjan offers structured diagnostic pathways and clinical management for cranial, spinal, and peripheral nerve disorders.</p>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Specialized Surgical Procedures</h2>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-3'>
          <li><strong>Minimally Invasive Spine Procedures</strong>
            <ul className='list-circle pl-6 mt-1 space-y-1 text-base'>
              <li>Tubular microdiscectomy for lumbar disc herniation</li>
              <li>Spinal canal decompression for lumbar canal stenosis</li>
              <li>Percutaneous and minimally invasive spinal stabilization</li>
            </ul>
          </li>
          <li><strong>Cranial & Brain Tumor Surgery</strong>
            <ul className='list-circle pl-6 mt-1 space-y-1 text-base'>
              <li>Microsurgical resection of brain tumors and meningiomas</li>
              <li>Endoscopic skull base approaches for pituitary lesions</li>
              <li>Intraoperative monitoring for preservation of critical motor pathways</li>
            </ul>
          </li>
          <li><strong>Neurovascular & Emergency Interventions</strong>
            <ul className='list-circle pl-6 mt-1 space-y-1 text-base'>
              <li>Clinical evaluation of cerebral aneurysms and vascular anomalies</li>
              <li>Emergency decompression for acute traumatic brain injury</li>
            </ul>
          </li>
        </ul>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Hospital Clinical Infrastructure</h2>
        <p className='text-lg text-gray-700 mb-3'>Surgical procedures and inpatient care are conducted at Sarvodaya Hospital, Greater Noida West, equipped with:</p>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-2'>
          <li>High-field neuroimaging (MRI and CT) for precise pre-operative diagnosis</li>
          <li>Neuronavigation systems for 3D intraoperative surgical tracking</li>
          <li>Intraoperative neurophysiological monitoring (IONM) to safeguard nerve tracts</li>
          <li>High-magnification surgical operating microscopes</li>
          <li>Specialized neuro-intensive care unit (Neuro-ICU) staffed 24/7</li>
        </ul>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Neurological Conditions Evaluated</h2>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-2'>
          <li>Degenerative spine disorders, sciatica, and cervical myelopathy</li>
          <li>Spinal cord tumors and spinal dysraphism</li>
          <li>Trigeminal neuralgia and cranial nerve compression</li>
          <li>Hydrocephalus and cerebrospinal fluid circulation disorders</li>
          <li>Traumatic brain injuries and spinal column fractures</li>
        </ul>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Spine Surgery & Complex Reconstruction</h2>
        <p className='text-lg text-gray-700 leading-relaxed mb-3'>Dr. Gunjan evaluates patients requiring complex spinal intervention after conservative medical treatments prove insufficient:</p>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-2'>
          <li>Spinal instrumentation and fusion for instability</li>
          <li>Surgical evaluation of vertebral fractures and traumatic spine injury</li>
          <li>Spinal decompression for nerve root impingement</li>
          <li>Management of spinal infections and epidural collections</li>
        </ul>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Pediatric Neurological Care</h2>
        <p className='text-lg text-gray-700 leading-relaxed mb-3'>Providing specialized evaluations for children presenting with neurological and spinal conditions:</p>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-2'>
          <li>Congenital malformations of the brain and spine</li>
          <li>Pediatric hydrocephalus and shunt management</li>
          <li>Spina bifida and spinal cord tethering</li>
          <li>Pediatric cranial tumors</li>
        </ul>
      </section>

      <section className='mb-8'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-4'>Structured Rehabilitation Pathways</h2>
        <p className='text-lg text-gray-700 leading-relaxed mb-3'>Postoperative care incorporates coordinated rehabilitation to support recovery:</p>
        <ul className='list-disc pl-6 text-lg text-gray-700 space-y-2'>
          <li>Postoperative physiotherapy protocols focused on mobility and core stability</li>
          <li>Ergonomic guidance for desk workers and manual laborers</li>
          <li>Regular clinical follow-ups to track neurological recovery</li>
        </ul>
      </section>

      <section className='mb-8 bg-blue-50 p-6 rounded-2xl border border-blue-100'>
        <h2 className='text-2xl md:text-3xl font-semibold mb-3 text-blue-900'>Consultation Details for Bulandshahr Residents</h2>
        <p className='text-lg text-gray-700 leading-relaxed mb-4'>
          To arrange a neurosurgical consultation with Dr. Gangesh Gunjan, please call{' '}
          <a href="tel:+917727834367" className='text-blue-600 font-semibold hover:underline'>+91-7727834367</a>{' '}
          or visit the Department of Neurosurgery, Room no. 1011, Sarvodaya Hospital, Sector 16C, Gaur City 2, Greater Noida West.
        </p>
      </section>
    </div>
  );
};

export default BulandshahrPage;